/**
 * Mapea insignias (badges) a una lista de productos replicando la lógica del
 * build estático (gatsby-node): por producto específico, por categoría y por
 * tag. Se usa en la búsqueda en vivo y otras pantallas que arman productos
 * desde la API (que no trae las insignias ya resueltas).
 *
 * @param {Array} products  Productos (cada uno con internal_id, categories[], tags[]).
 * @param {Array} insignias Insignias desde /api/insignias (con badge_rules[]).
 * @returns {Array} productos con la propiedad `insignias` poblada.
 */
// Compara ids sin depender del tipo. Es imprescindible: los productos que vienen del
// pageContext/nodos de Gatsby traen los ids como STRING (el schema los infiere así,
// p.ej. categories[].id === "30"), mientras que las reglas de /api/insignias los traen
// como NUMBER (30). Con === estricto nunca cruzaban y las insignias por categoría se
// perdían, haciendo desaparecer el badge de la ficha de producto.
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

export const attachInsigniasToProducts = (products = [], insignias = []) => {
  if (!Array.isArray(products) || products.length === 0) return products || [];
  if (!Array.isArray(insignias) || insignias.length === 0) {
    return products.map((p) => ({ ...p, insignias: p.insignias || [] }));
  }

  const map = {}; // internal_id -> [insignia]
  const add = (internalId, insignia) => {
    if (internalId == null) return;
    if (!map[internalId]) map[internalId] = [];
    if (!map[internalId].some((i) => sameId(i.id, insignia.id))) {
      map[internalId].push(insignia);
    }
  };

  insignias.forEach((insignia) => {
    const rules = insignia.badge_rules || insignia.badgeRules || [];
    rules.forEach((rule) => {
      // Regla por producto específico (ruleProduct.id === product.internal_id)
      (rule.products || []).forEach((rp) => {
        products.forEach((p) => {
          if (sameId(p.internal_id, rp.id)) add(p.internal_id, insignia);
        });
      });
      // Regla por categoría
      (rule.categories || []).forEach((rc) => {
        products.forEach((p) => {
          if ((p.categories || []).some((c) => sameId(c.id, rc.id))) {
            add(p.internal_id, insignia);
          }
        });
      });
      // Regla por tag (por slug o nombre, como en el build)
      (rule.tags || []).forEach((rt) => {
        products.forEach((p) => {
          if ((p.tags || []).some((t) => t.slug === rt.slug || t.name === rt.name)) {
            add(p.internal_id, insignia);
          }
        });
      });
    });
  });

  return products.map((p) => ({ ...p, insignias: map[p.internal_id] || [] }));
};
